import { useToast, type ToastOptions } from "@nabble/ui";
import { useState } from "react";
import { resetUserMfa, resetUserPassword, updateUserStatus, type AdminUser } from "../../../api/adminUsers";
import type { UnsentLink } from "../../../components/ShareLinkDialog";
import { errorMessage } from "../../../errors";
import type { UserStatus } from "../../../model/userStatus";

/** An action on a user that waits for the administrator to confirm it. */
export type PendingUserAction =
  | { kind: "mfa"; user: AdminUser }
  | { kind: "status"; user: AdminUser; status: UserStatus }
  | { kind: "password"; user: AdminUser };

type Show = (options: ToastOptions) => void;

async function resetMfa(user: AdminUser, show: Show): Promise<void> {
  await resetUserMfa(user.id);
  show({ title: "Second factor reset", text: `${user.displayName} will need to set it up again at their next sign-in.`, tone: "success" });
}

async function changeStatus(user: AdminUser, status: UserStatus, show: Show): Promise<void> {
  await updateUserStatus(user.id, status);
  show({
    title: status === "DISABLED" ? "User disabled" : "User activated",
    text: status === "DISABLED" ? "Their open sessions were closed." : undefined,
    tone: "success",
  });
}

async function sendPasswordLink(user: AdminUser, show: Show, shareLink: (unsent: UnsentLink) => void): Promise<void> {
  const result = await resetUserPassword(user.id);
  if (!result.emailSent && result.link) shareLink({ link: result.link, email: user.email });
  else show({ title: "Link sent", text: `We sent ${user.email} a link to reset their password.`, tone: "success" });
}

export interface PendingUserActionState {
  pending: PendingUserAction | null;
  confirm: (action: PendingUserAction) => void;
  cancel: () => void;
  /** Runs the confirmed action; on success it closes the dialog and calls `onDone`. */
  run: () => Promise<void>;
}

/** Holds the action waiting for confirmation and runs it once confirmed. */
export function usePendingUserAction(onDone: () => void, shareLink: (unsent: UnsentLink) => void): PendingUserActionState {
  const { show } = useToast();
  const [pending, setPending] = useState<PendingUserAction | null>(null);

  async function perform(action: PendingUserAction): Promise<void> {
    if (action.kind === "mfa") await resetMfa(action.user, show);
    else if (action.kind === "status") await changeStatus(action.user, action.status, show);
    else await sendPasswordLink(action.user, show, shareLink);
  }

  async function run(): Promise<void> {
    if (!pending) return;
    try {
      await perform(pending);
      setPending(null);
      onDone();
    } catch (err) {
      show({ title: "We couldn't complete the action", text: errorMessage(err, "Please try again."), tone: "danger" });
    }
  }

  return { pending, confirm: setPending, cancel: () => setPending(null), run };
}
